/**
 * @param {string} raw
 * @returns {string}
 */
function sanitizeNumPalletsInput(raw) {
    if (raw === '') {
        return '';
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return '';
    }

    return String(Math.floor(parsed));
}

/**
 * @param {number} index zero-based pallet index
 * @returns {string} one-based display number (e.g. "1", "2")
 */
function getPalletLabel(index) {
    return String(index + 1);
}

/**
 * @param {Object} [options]
 * @param {Array<{ id: number }>} [options.companySourceOptions]
 * @param {boolean} [options.isFbmMode]
 * @returns {{ id: string, companySourceId: number | null, numCases: number, expiration: string, supplierLot: string }}
 */
function createEmptyLine({ companySourceOptions = [], isFbmMode = false } = {}) {
    let companySourceId = null;
    if (!isFbmMode && companySourceOptions.length === 1) {
        companySourceId = companySourceOptions[0].id;
    }

    return {
        id: crypto.randomUUID(),
        companySourceId,
        numCases: 0,
        expiration: '',
        supplierLot: '',
    };
}

/**
 * @param {Object} [options]
 * @param {Array<{ id: number }>} [options.companySourceOptions]
 * @param {boolean} [options.isFbmMode]
 * @returns {{ id: string, lines: ReturnType<typeof createEmptyLine>[] }}
 */
function createEmptyPallet({ companySourceOptions = [], isFbmMode = false } = {}) {
    return {
        id: crypto.randomUUID(),
        lines: [createEmptyLine({ companySourceOptions, isFbmMode })],
    };
}

/**
 * @param {ReturnType<typeof createEmptyPallet>[]} pallets
 * @param {number} numPallets
 * @param {Object} [options]
 * @param {Array<{ id: number }>} [options.companySourceOptions]
 * @param {boolean} [options.isFbmMode]
 * @returns {ReturnType<typeof createEmptyPallet>[]}
 */
function syncPalletsToCount(pallets, numPallets, { companySourceOptions = [], isFbmMode = false } = {}) {
    const count = Math.max(0, Math.floor(Number(numPallets) || 0));
    if (count === 0) {
        return [];
    }

    const next = pallets.slice(0, count);
    while (next.length < count) {
        next.push(createEmptyPallet({ companySourceOptions, isFbmMode }));
    }
    return next;
}

/**
 * Fills unset companySourceId on existing lines when exactly one source option is available.
 *
 * @param {ReturnType<typeof createEmptyPallet>[]} pallets
 * @param {Object} [options]
 * @param {Array<{ id: number }>} [options.companySourceOptions]
 * @param {boolean} [options.isFbmMode]
 * @returns {ReturnType<typeof createEmptyPallet>[]}
 */
function autoFillSingleCompanySourceOnPallets(pallets, { companySourceOptions = [], isFbmMode = false } = {}) {
    if (isFbmMode || companySourceOptions.length !== 1 || pallets.length === 0) {
        return pallets;
    }

    const sourceId = companySourceOptions[0].id;
    let didUpdate = false;

    const next = pallets.map((pallet) => ({
        ...pallet,
        lines: pallet.lines.map((line) => {
            if (line.companySourceId != null) {
                return line;
            }
            didUpdate = true;
            return { ...line, companySourceId: sourceId };
        }),
    }));

    return didUpdate ? next : pallets;
}

/**
 * @param {string | number | undefined | null} raw
 * @returns {number | undefined}
 */
function parseNonNegativeNumCases(raw) {
    if (raw === '' || raw === undefined || raw === null) {
        return undefined;
    }

    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed < 0) {
        return undefined;
    }

    return parsed;
}

/**
 * @typedef {Object} QuickFillValues
 * @property {number} [companySourceId]
 * @property {number} [numCases]
 * @property {string} [expiration]
 * @property {string} [supplierLot]
 */

/**
 * @param {ReturnType<typeof createEmptyPallet>[]} pallets
 * @param {QuickFillValues} values
 * @param {boolean} isFbmMode
 * @returns {ReturnType<typeof createEmptyPallet>[]}
 */
function applyQuickFillToPallets(pallets, values, isFbmMode) {
    return pallets.map((pallet) => ({
        ...pallet,
        lines: pallet.lines.map((line) => {
            const next = { ...line };

            if (!isFbmMode && values.companySourceId !== undefined) {
                next.companySourceId = values.companySourceId;
            }
            if (values.numCases !== undefined) {
                const numCases = parseNonNegativeNumCases(values.numCases);
                if (numCases !== undefined) {
                    next.numCases = numCases;
                }
            }
            if (values.expiration !== undefined) {
                next.expiration = values.expiration;
            }
            if (values.supplierLot !== undefined) {
                next.supplierLot = values.supplierLot;
            }

            return next;
        }),
    }));
}

/**
 * @param {ReturnType<typeof createEmptyPallet>[]} pallets
 * @param {{ type: string, [key: string]: unknown }} action
 * @returns {ReturnType<typeof createEmptyPallet>[]}
 */
function palletStagingReducer(pallets, action) {
    switch (action.type) {
        case 'SYNC_NUM_PALLETS':
            return syncPalletsToCount(pallets, Number(action.numPallets) || 0, {
                companySourceOptions: /** @type {Array<{ id: number }>} */ (action.companySourceOptions) || [],
                isFbmMode: Boolean(action.isFbmMode),
            });
        case 'AUTO_FILL_SINGLE_SOURCE':
            return autoFillSingleCompanySourceOnPallets(pallets, {
                companySourceOptions: /** @type {Array<{ id: number }>} */ (action.companySourceOptions) || [],
                isFbmMode: Boolean(action.isFbmMode),
            });
        case 'ADD_LINE':
            return pallets.map((pallet) =>
                pallet.id === action.palletId
                    ? {
                          ...pallet,
                          lines: [
                              ...pallet.lines,
                              createEmptyLine({
                                  companySourceOptions:
                                      /** @type {Array<{ id: number }>} */ (action.companySourceOptions) || [],
                                  isFbmMode: Boolean(action.isFbmMode),
                              }),
                          ],
                      }
                    : pallet,
            );
        case 'REMOVE_LINE':
            return pallets.map((pallet) => {
                if (pallet.id !== action.palletId || pallet.lines.length <= 1) {
                    return pallet;
                }
                return {
                    ...pallet,
                    lines: pallet.lines.filter((line) => line.id !== action.lineId),
                };
            });
        case 'UPDATE_LINE':
            return pallets.map((pallet) =>
                pallet.id === action.palletId
                    ? {
                          ...pallet,
                          lines: pallet.lines.map((line) =>
                              line.id === action.lineId ? { ...line, .../** @type {object} */ (action.patch) } : line,
                          ),
                      }
                    : pallet,
            );
        case 'APPLY_QUICK_FILL':
            return applyQuickFillToPallets(
                pallets,
                /** @type {QuickFillValues} */ (action.values),
                Boolean(action.isFbmMode),
            );
        default:
            return pallets;
    }
}

export {
    applyQuickFillToPallets,
    autoFillSingleCompanySourceOnPallets,
    createEmptyLine,
    createEmptyPallet,
    getPalletLabel,
    palletStagingReducer,
    parseNonNegativeNumCases,
    sanitizeNumPalletsInput,
    syncPalletsToCount,
};
